// This component provides list of all tabs for a given playbook

import { useState, useRef, useEffect } from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { TabTilPropsSelectedDatasetList } from '../../redux/TabTile/TabTilePropsInterfaces';
import { toggleTabHidden } from '../../redux/TabTile/TabActions';

import { TabRibbonProps, TabRibbonStateProps } from './TabRibbonInterfaces';
import { IndTabs } from '../../redux/TabTile/TabStateInterfaces';
import {
  actionsToEnableRenameTab,
  actionsToRenameTab,
  actionsToSelectTab,
} from '../../redux/TabTile/actionsTabTile';
import {
  actionsToAddTab,
  actionsToRemoveTab,
  actionsToUpdateSelectedTile,
} from '../../redux/TabTile/TabTileActionsAndMultipleDispatches';
import AddIcon from '@mui/icons-material/Add';
import {
  addChartFilterTabTileName,
  updateChartFilterSelectedGroups,
} from '../../redux/ChartFilterGroup/ChartFilterGroupStateActions';
import Logger from '../../Logger';
import ArrowLeftIcon from '@mui/icons-material/ArrowLeft';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Tooltip, Menu, MenuItem } from '@mui/material';
import TabList from './TabList';
import {
  callQueryAPI,
  setColorScheme,
  setTileThemeId,
} from '../../redux/ChartPoperties/ChartControlsActions';
import {
  setDashColorScheme,
  setTheme,
  toggleAllTabs,
  toggleAllTiles,
  toggleThemeState,
  setThemeId,
  reOrderTabList,
} from '../../redux/TabTile/TabActions';
import { useDispatch } from 'react-redux';
import {
  updateUserSelection,
  updateUserSelectionColumn,
} from '../../redux/ChartPoperties/ChartControlsActions';
import fetchThemeIdByName from '../../hooks/fetchThemeIdByName';
import { ColorSchemes } from '../ChartOptions/Color/ColorScheme';
import { NotificationDialog } from '../CommonFunctions/DialogComponents';
import { AlertColor } from '@mui/material';
import fetchUserThemes from '../../api/fetchUserThemes';
import { setDefaultThemeId } from '../../redux/TabTile/TabActions';

const TabRibbon = ({
  selectedTab,
  backgroundColor,
  tabList,
  tabAreaBackgroundColor,
  iconColor,
  selectedTabBackgroundColor,

  // state
  tabTileProps,
  tabState,
  // tileState,
  // tableData,
  chartProp,
  chartGroup,
  chartControls,

  // dispatch
  addTab,
  selectTab,
  removeTab,
  updateChartFilterSelectedGroups,
  setColorScheme,
  setTileThemeId,
  setDashColorScheme,
  setThemeId,
  toggleAllTabs,
  toggleAllTiles,
  toggleThemeState,
  enableRenameTab,
  completeRenameTab,
  selectTile,
  addChartFilterTabTileName,
  toggleTabHidden,
}: TabRibbonProps) => {
  const dispatch = useDispatch();
  const addReportFilterGroup = (nextPropKey: string) => {
    var propKey: string = `${tabTileProps.selectedTabId}.${tabTileProps.selectedTileId}`;

    // let selectedFilterGroups = chartGroup.tabTile[propKey] || [];
    let selectedDatasetID = chartProp.properties[propKey].selectedDs.id;

    //	if (!(selectedFilterGroups && selectedFilterGroups.length > 0)) {
    addChartFilterTabTileName(selectedDatasetID, nextPropKey);
    if (chartGroup.groups && Object.keys(chartGroup.groups).length > 0) {
      updateChartFilterSelectedGroups(nextPropKey, chartGroup.tabTile[propKey][0]);
    }
    ///	}
  };

  const [notification, setNotification] = useState({
    open: false,
    severity: 'warning' as AlertColor,
    message: '',
  });
  const showNotification = (message: string, severity: AlertColor = 'warning') => {
    setNotification({
      open: true,
      severity,
      message,
    });
  };

  const handleCloseNotification = () => {
    setNotification((prev) => ({ ...prev, open: false }));
  };

  const MAX_TABS = 20;
  const handleAddTab = async () => {
    if (tabState.tabList.length >= MAX_TABS) {
      showNotification(`Maximum tab limit reached (${MAX_TABS} tabs)`);
      return;
    }
    const tabId: number = tabTileProps.nextTabId;
    const propKey: string = `${tabTileProps.selectedTabId}.${tabTileProps.selectedTileId}`;
    const newPropKey: string = `${tabId}.${1}`;

    addTab(
      tabId,
      tabTileProps.selectedTable,
      chartProp.properties[propKey].selectedDs,
      chartProp.properties[propKey].selectedTable,
    );

    queueMicrotask(() => {
      handleNewTab(tabId, propKey, newPropKey);
    });
  };

  const handleNewTab = async (tabId: number, propKey: string, newPropKey: string) => {
    const newTab = tabState.tabs[tabId - 1];

    // Determine which color scheme to use for the new tab:
    // - If "Across Entire Playbook" is enabled on the previous tab (allTabs),
    //   propagate its color scheme (so dashboard + tiles stay in sync).
    // - Otherwise, fall back to the user's default theme (or other default),
    //   so a new tab does NOT inherit a per-tab-only choice from the previous tab.
    let colorScheme: string | undefined;

    if (newTab?.dashboardState?.allTabs) {
      colorScheme = newTab.dashboardState.colorScheme;
    }

    if (!colorScheme) {
      const { themes, defaultThemeId } = await fetchUserThemes();

      if (defaultThemeId) {
        const defaultTheme = themes.find((t) => t.themeId === defaultThemeId);
        if (defaultTheme) {
          colorScheme = defaultTheme.themeName;
        }
      }
    }

    // - Dashboard background/theme reflects it
    // - Any new tiles in this tab pick up the correct scheme
    if (colorScheme) {
      // Set dashboard-level color scheme so the new tab's dashboard
      // background and theme are correct.
      setDashColorScheme(colorScheme, tabId);
      // Also setting the color scheme for the first tile in the new tab so that
      // charts immediately use the same theme (especially when "Across Entire
      // Playbook" is enabled).
      setColorScheme(newPropKey, colorScheme);
    }

    if (newTab?.dashboardState?.allTabs === true) {
      // Preserveing "Across Entire Playbook" behavior for new tabs
      toggleAllTabs(tabId);
    }
    // Do NOT propagating "Across Current Tab" (allTiles) setting to new tabs.
    // New tabs should default to "Current Dashboard only" unless the user
    // explicitly opts into playbook-wide behavior (allTabs).
    if (newTab?.dashboardState?.themeState === true) {
      toggleThemeState(tabId);
    }
    if (
      chartControls?.properties[propKey]?.rowSecurity?.filterPanels[
        chartControls?.properties?.[propKey]?.rowSecurity?.currentOperator?.toLowerCase()
      ]?.filters.length > 0
    ) {
      dispatch(
        updateUserSelection(
          propKey,
          chartControls?.properties?.[propKey]?.rowSecurity?.currentOperator.toLowerCase(),
          [],
        ),
      );
      dispatch(callQueryAPI(propKey, true));
    }
    if (
      chartControls?.properties[propKey]?.columnSecurity?.filterPanels[
        chartControls?.properties?.[propKey]?.columnSecurity?.currentOperator.toLowerCase()
      ]?.filters.length > 0
    ) {
      dispatch(
        updateUserSelectionColumn(
          propKey,
          chartControls?.properties?.[propKey]?.columnSecurity?.currentOperator.toLowerCase(),
          [],
        ),
      );
      dispatch(callQueryAPI(propKey, true));
    }

    try {
      const { defaultThemeId } = await fetchUserThemes();

      dispatch(setDefaultThemeId(tabId, defaultThemeId));
    } catch (err) {
      console.error('Failed to apply default theme on add tab:', err);
    }

    // Set themeId: use directly for silzila themes, fetch for user themes
    if (colorScheme) {
      const silzilaThemes = ColorSchemes.map((theme: any) => theme.name);
      if (silzilaThemes.includes(colorScheme)) {
        setThemeId(colorScheme, tabId); // Use name directly
        setTileThemeId(newPropKey, colorScheme);
      } else {
        try {
          const themeId = await fetchThemeIdByName(colorScheme);
          if (themeId) {
            setThemeId(themeId, tabId);
            setTileThemeId(newPropKey, themeId);
          }
        } catch (err) {
          console.error('Failed to fetch themeId:', err);
        }
      }
    }

    addReportFilterGroup(`${tabId}.${1}`);
  };

  const handleSelectTab = (tabName: string, tabId: number) => {
    // handle how to get selected tile for the switching tab and update it in two places - tabTileProps and tabState
    let tabObj: IndTabs = tabState.tabs[tabId];
    if (tabTileProps.dashMode !== 'Edit' && tabObj.hidden) {
      return; // do nothing
    }

    // changes:
    //  added showDashBoard(tabObj.tabId, tabObj.showDash); in dataviewer comp under onchange
    // once tabtileProps-> dashmode set to present then that remain same for all the tabs that can be selected after this
    // once tabTileprops-> dashmode set to "Edit" then that mode remain same for all tabs that can be selected after this
    // ...but tabtileProps->showdash will change according to individual tab prop(tabstate->tabs->tabid-> showdash)

    let callAPIForSecurity = false;
    if (tabTileProps.selectedTabId !== tabId) {
      callAPIForSecurity = true;
    }

    if (tabTileProps.dashMode === 'Present') {
      selectTab(tabName, tabId, true, 'Present');
    } else {
      selectTab(tabName, tabId, tabObj.showDash, tabTileProps.dashMode);
    }

    let tileName: string = tabObj.selectedTileName;
    let tileId: number = tabObj.selectedTileId;
    let nextTileId: number = tabObj.nextTileId;

    let propKey: string = `${tabId}.${tileId}`;
    // let chartObj: IndChartPropProperties = chartProp.properties[propKey];

    if (
      chartControls?.properties[propKey]?.rowSecurity?.filterPanels[
        chartControls?.properties?.[propKey]?.rowSecurity?.currentOperator?.toLowerCase()
      ]?.filters.length > 0
    ) {
      dispatch(
        updateUserSelection(
          propKey,
          chartControls?.properties?.[propKey]?.rowSecurity?.currentOperator.toLowerCase(),
          [],
        ),
      );
      if (callAPIForSecurity) {
        dispatch(callQueryAPI(propKey, true));
      }
    }
    if (
      chartControls?.properties[propKey]?.columnSecurity?.filterPanels[
        chartControls?.properties?.[propKey]?.columnSecurity?.currentOperator.toLowerCase()
      ]?.filters.length > 0
    ) {
      dispatch(
        updateUserSelectionColumn(
          propKey,
          chartControls?.properties?.[propKey]?.columnSecurity?.currentOperator.toLowerCase(),
          [],
        ),
      );
      if (callAPIForSecurity) {
        dispatch(callQueryAPI(propKey, true));
      }
    }

    selectTile(tabId, tileName, tileId, nextTileId, true);
    setTimeout(() => {
      handleClose();
    }, 300);
  };

  // This performs the actual deletion after confirmation
  const handleRemoveTab = async (tabName: string, tabId: number) => {
    // getting params to pass for removeTab dispatch
    let tabToRemoveIndex: number = tabState.tabList.findIndex((item: number) => item === tabId);
    let selectedTab: number = tabTileProps.selectedTabId;
    let addingNewTab: boolean = false;

    // Selecting which tab to highlight next
    // if we are removing a tab that is currently selected, pick another tab before or after to highlight.
    // Else no change in highlighting tabs
    if (tabId === selectedTab) {
      // choosing next selection, move left
      let nextSelection: number = tabToRemoveIndex - 1;

      // if this is the first tab, move right
      if (nextSelection < 0) {
        // if this is the only tab in the work area
        if (tabState.tabList.length === 1) {
          addingNewTab = true;
          await handleAddTab();
        }

        // if there are more than one tab
        else {
          nextSelection = 1;
        }
      }

      // choosing appropriate dispatch based on whether we are adding a tab or not
      if (addingNewTab) {
        removeTab(tabName, tabId, tabToRemoveIndex);
      } else {
        Logger('info', 'case');
        let newTabId: number = tabState.tabList[nextSelection];
        let newObj: IndTabs = tabState.tabs[newTabId];

        removeTab(tabName, tabId, tabToRemoveIndex, newObj);
      }
    } else {
      removeTab(tabName, tabId, tabToRemoveIndex);
    }
  };

  // called when tabName is doubleClicked
  const handleRenameTabBegin = (tabId: number) => {
    enableRenameTab(tabId, true);
  };

  // called when renaming tab is complete
  const handleRenameTabComplete = (renameValue: string, tabId: number) => {
    // enableRenameTab(tabId, false);
    completeRenameTab(renameValue, tabId);
  };

  // const tablist = tabState.tabList.map((tab: number) => {
  //   let currentObj: IndTabs = tabState.tabs[tab];
  //   return (
  //     <IndividualTab
  //       key={currentObj.tabId}
  //       tabName={currentObj.tabName}
  //       editing={tabTileProps.editTabName}
  //       editingTabId={tabTileProps.editingTabId}
  //       selectedTab={tabTileProps.selectedTabId}
  //       tabId={currentObj.tabId}
  //       // actions to call back
  //       selectTab={handleSelectTab}
  //       removeTab={handleRemoveTab}
  //       renameTabBegin={handleRenameTabBegin}
  //       renameTabComplete={handleRenameTabComplete}
  //       //showdash prop
  //       showDash={tabTileProps.showDash}
  //       dashMode={tabTileProps.dashMode}
  //     />
  //   );
  // });

  const ITEM_HEIGHT = 34;
  const [anchorEl, setAnchorEl] = useState<any>(null);
  const tabOpen = Boolean(anchorEl);
  const [currentTabLength, setCurrentTabLength] = useState<number>(0);
  const tabWrapperRef = useRef<HTMLDivElement>(null);

  const [isOverflowing, setIsOverflowing] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const checkOverflow = () => {
    if (tabWrapperRef.current && contentRef.current) {
      const bufferPixels = 10;

      const isContentOverflowing =
        contentRef.current.scrollWidth >= tabWrapperRef.current.clientWidth - bufferPixels;

      setIsOverflowing(isContentOverflowing);
    }
  };

  const handleTabScrollOnNewTabAdd = () => {
    setTimeout(() => {
      if (contentRef.current && tabWrapperRef.current) {
        tabWrapperRef.current.scrollLeft = contentRef.current.scrollWidth;
      }
    }, 50);
  };

  useEffect(() => {
    window.addEventListener('resize', () => {
      checkOverflow();
    });

    return () => {
      window.removeEventListener('resize', () => {});
    };
  }, []);

  useEffect(() => {
    checkOverflow();
  }, [tabState.tabList, tabTileProps.dashMode]);

  useEffect(() => {
    setCurrentTabLength(tabState.tabList.length);
  }, [tabState.tabList]);

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleTabScroll = (step: number) => {
    tabWrapperRef.current!.scrollLeft += step;
  };

  const handleReOrderTabList = (moveAction: string, moveItemInd: number) => {
    dispatch(reOrderTabList(moveAction, moveItemInd));
  };

  return (
    <div
      style={{
        display: 'flex',
        overflow: 'hidden',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        backgroundColor: backgroundColor,
      }}
    >
      <div
        style={{
          display: 'flex',
          overflowX: 'hidden',
          alignItems: 'center',
          width: '100%',
          gap: '4px',
          padding: tabTileProps.dashMode === 'TV Display' ? '10px' : '',
        }}
      >
        {tabTileProps.dashMode !== 'TV Display' && (
          <>
            <Tooltip
              title="Display Tab List"
              slotProps={{
                tooltip: {
                  sx: {
                    fontSize: '12px',
                  },
                },
              }}
            >
              <KeyboardArrowDownIcon
                style={{
                  fontSize: '20px',
                  background: tabAreaBackgroundColor,
                  color: iconColor,
                  margin: '0px',
                  height: '1.75rem',
                  width: '1.6rem',
                  border: `1px solid ${iconColor === '#ffffff' ? 'rgba(255,255,255,255)' : 'rgba(0,0,0,0.1)'}`,
                  borderRadius: '3px',
                }}
                onClick={(e) => {
                  setAnchorEl(e.currentTarget);
                }}
              />
            </Tooltip>
            <Menu
              id="long-menu"
              MenuListProps={{
                'aria-labelledby': 'long-button',
              }}
              anchorEl={anchorEl}
              open={tabOpen}
              onClose={handleClose}
              slotProps={{
                paper: {
                  style: {
                    minHeight: ITEM_HEIGHT * 4.5,
                    maxHeight: ITEM_HEIGHT * 12.3,
                    width: '26ch',
                    margin: '11px 0px 0px 1px',
                    padding: '0',
                    boxShadow: `${iconColor === '#ffffff' ? '0px 2px 4px rgba(184, 159, 159, 0.5)' : '0px 2px 4px rgba(0, 0, 0, 0.2)'}`,
                    backgroundColor: tabAreaBackgroundColor,
                  },
                },
              }}
            >
              <TabList
                tabList={tabState.tabList}
                tabState={tabState}
                tabTileProps={tabTileProps}
                reOrderTabList={handleReOrderTabList}
                handleClose={handleClose}
                handleSelectTab={handleSelectTab}
                handleRemoveTab={handleRemoveTab}
                handleRenameTabBegin={handleRenameTabBegin}
                toggleTabHidden={toggleTabHidden}
                handleRenameTabComplete={handleRenameTabComplete}
                tabAreaBackgroundColor={tabAreaBackgroundColor}
                iconColor={iconColor}
                selectedTabBackgroundColor={selectedTabBackgroundColor}
                inPopup={true}
              />
              {/* {tablist.map((tabItem) => (
            <MenuItem
            style={{
              width: '100%',
              margin: '0px',
              padding: '0px',
              height: '35px',
              backgroundColor: 'transparent',
              }}
              onClick={handleClose}
              >
              {React.cloneElement(tabItem, { popupClass: 'popupTab', inPopup: true })}
              </MenuItem>
              ))} */}
            </Menu>
          </>
        )}
        <div
          style={{ overflow: 'hidden', display: 'flex', alignItems: 'center', width: '100%' }}
          ref={tabWrapperRef}
        >
          <TabList
            tabList={tabList || tabState.tabList}
            tabState={tabState}
            tabTileProps={tabTileProps}
            selectedTab={selectedTab}
            backgroundColor={backgroundColor}
            reOrderTabList={handleReOrderTabList}
            handleSelectTab={handleSelectTab}
            handleRemoveTab={handleRemoveTab}
            toggleTabHidden={toggleTabHidden}
            handleRenameTabBegin={handleRenameTabBegin}
            handleRenameTabComplete={handleRenameTabComplete}
            tabAreaBackgroundColor={tabAreaBackgroundColor}
            iconColor={iconColor}
            selectedTabBackgroundColor={selectedTabBackgroundColor}
            childRef={contentRef}
          />
          {/* {tablist} */}
          {tabTileProps.dashMode === 'Edit' ? (
            <span
              title={
                tabState.tabList.length >= MAX_TABS ? 'Maximum 20 tabs allowed' : 'Create a new tab'
              }
              className="plusTab"
              style={{
                width: '30px',
                height: '27.2px',
                visibility: isOverflowing ? 'hidden' : 'visible',
                color: iconColor,
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
              onClick={() => {
                handleAddTab();
                handleTabScrollOnNewTabAdd();
              }}
            >
              <AddIcon sx={{ fontSize: '16px' }} />
            </span>
          ) : null}
        </div>
      </div>

      <div style={{ display: 'flex' }}>
        {isOverflowing && (
          <div
            style={{
              visibility: isOverflowing ? 'visible' : 'hidden',
              marginLeft: '4px',
              gap: '4px',
              display: 'flex',
              justifyContent: 'flex-end',
              overflow: 'hidden',
              alignItems: 'center',
            }}
          >
            {tabTileProps.dashMode === 'Edit' && (
              <span
                title="Create a new tab"
                className="plusTab"
                style={{
                  cursor: 'pointer',
                  width: '30px',
                  height: '27.2px',
                  visibility: isOverflowing ? 'visible' : 'hidden',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
                onClick={() => {
                  handleAddTab();
                  handleTabScrollOnNewTabAdd();
                }}
              >
                <AddIcon sx={{ fontSize: '16px' }} />
              </span>
            )}

            <ArrowLeftIcon
              style={{
                cursor: 'pointer',
              }}
              onClick={() => handleTabScroll(-200)}
            />
            <ArrowRightIcon
              style={{
                cursor: 'pointer',
                marginRight: '4px',
              }}
              onClick={() => handleTabScroll(200)}
            />
          </div>
        )}
      </div>

      <NotificationDialog
        openAlert={notification.open}
        severity={notification.severity}
        testMessage={notification.message}
        onCloseAlert={handleCloseNotification}
      />
    </div>
  );
};

const mapStateToProps = (state: TabRibbonStateProps) => {
  return {
    tabTileProps: state.tabTileProps,
    tabState: state.tabState,
    chartProp: state.chartProperties,
    chartGroup: state.chartFilterGroup,
    chartControls: state.chartControls,
  };
};

const mapDispatchToProps = (dispatch: Dispatch<any>) => {
  return {
    // ###########################################################
    // Tab related dispatch methods
    // ###########################################################
    addTab: (
      tabId: number,
      table: any,
      selectedDs: TabTilPropsSelectedDatasetList,
      selectedTablesInDs: any,
    ) => dispatch(actionsToAddTab({ tabId, table, selectedDs, selectedTablesInDs })),
    selectTab: (tabName: string, tabId: number, showDash: boolean, dashMode: string) =>
      dispatch(actionsToSelectTab(tabName, tabId, showDash, dashMode)),
    removeTab: (tabName: string, tabId: number, tabToRemoveIndex: number, newObj?: any) =>
      dispatch(actionsToRemoveTab(tabName, tabId, tabToRemoveIndex, newObj)),
    updateChartFilterSelectedGroups: (groupId: string, filters: any) =>
      dispatch(updateChartFilterSelectedGroups(groupId, filters)),
    setColorScheme: (propKey: string, color: string) => dispatch(setColorScheme(propKey, color)),
    setTileThemeId: (propKey: string, themeId: string) =>
      dispatch(setTileThemeId(propKey, themeId)),
    setTheme: (theme: string, tabId: number) => dispatch(setTheme(theme, tabId)),
    setDashColorScheme: (colorScheme: string, tabId: number) =>
      dispatch(setDashColorScheme(colorScheme, tabId)),
    setThemeId: (themeId: string, tabId: number) => dispatch(setThemeId(themeId, tabId)),
    toggleAllTiles: (tabId: number) => dispatch(toggleAllTiles(tabId)),
    toggleAllTabs: (tabId: number) => dispatch(toggleAllTabs(tabId)),
    toggleThemeState: (tabId: number) => dispatch(toggleThemeState(tabId)),
    enableRenameTab: (tabId: number, isTrue: boolean) =>
      dispatch(actionsToEnableRenameTab(tabId, isTrue)),
    completeRenameTab: (renameValue: string, tabId: number) =>
      dispatch(actionsToRenameTab(renameValue, tabId)),
    // ###########################################################
    // Tile related dispatch methods
    // ###########################################################
    selectTile: (
      tabId: number,
      tileName: string,
      tileId: number,
      nextTileId: number,
      fromTab: boolean,
    ) => dispatch(actionsToUpdateSelectedTile(tabId, tileName, tileId, nextTileId, fromTab)),
    addChartFilterTabTileName: (selectedDatasetID: string, tabTileName: string) =>
      dispatch(addChartFilterTabTileName(selectedDatasetID, tabTileName)),
    toggleTabHidden: (tabId: number) => dispatch(toggleTabHidden(tabId)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(TabRibbon);
